import { StepType } from "@redotech/redo-model/advanced-flow/common";
import { ContactChannel } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { DefaultRule } from "../types";

/**
 * Shape of this rule's `configurations` JSONB blob. The merchant fills in
 * a list of exact email addresses; any inbound email whose sender matches
 * one of them archives the conversation.
 */
export const ArchiveEmailsFromBlockedSendersConfigSchema = Schema.Struct({
  blockedEmails: Schema.Array(Schema.String),
});

export type ArchiveEmailsFromBlockedSendersConfig =
  typeof ArchiveEmailsFromBlockedSendersConfigSchema.Type;

const bind = makeBind<ArchiveEmailsFromBlockedSendersConfig>();

/**
 * Default rule: Archive emails from blocked senders
 *
 * When an email arrives (gmail/outlook) from an address on the merchant's
 * blocklist, archive the conversation so it doesn't reach the inbox or
 * kick off downstream automations. Off by default — merchants add
 * addresses and enable.
 *
 * Runs before other MESSAGE_RECEIVED rules (`sortIndex: 1`) so the
 * archive happens before anything tries to reopen or respond.
 */
export const archiveEmailsFromBlockedSendersRule = {
  id: "default-archive-emails-from-blocked-senders",
  name: "Archive emails from blocked senders",
  description:
    "When an email arrives from an address on the blocklist, archive the conversation.",
  hidden: false,
  triggerType: RuleTriggerType.MESSAGE_RECEIVED,

  configSchema: ArchiveEmailsFromBlockedSendersConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-archive-emails-from-blocked-senders",
    enabled: false,
    sortIndex: 1,
    configurations: { blockedEmails: [] },
  },

  template: {
    name: "Archive emails from blocked senders",
    description:
      "When an email arrives from an address on the blocklist, archive the conversation.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.MESSAGE_RECEIVED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_CHANNEL,
              channels: ["gmail", "outlook"],
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.SENT_FROM,
              channel: ContactChannel.EMAIL,
              values: bind("blockedEmails", []),
              queryOperator: QueryOperator.IS_EXACTLY,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          { actionType: RuleActionType.SET_STATUS_V3, status: "ARCHIVED" },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule<string, ArchiveEmailsFromBlockedSendersConfig>;
